import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Leaf, Loader2, Inbox } from "lucide-react";

const KIND_FILTERS = ["all", "reflection", "wellbeing_shift", "streak", "quote", "milestone"];

export default function StoryWall() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("story_wall_public_title"));
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/story-wall", { params: filter === "all" ? {} : { kind: filter } });
      setMoments(data?.moments || []);
    } catch { setMoments([]); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="story-wall-public">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-foreground">{t("story_wall_public_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("story_wall_public_sub")}</p>
        </div>
        <button onClick={() => navigate("/app/story-wall/inbox")} data-testid="swp-inbox" className="flex shrink-0 items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
          <Inbox className="h-4 w-4" /> {t("story_wall_client_title")}
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {KIND_FILTERS.map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`swp-filter-${k}`}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${filter === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {t(k === "all" ? "story_wall_filter_all" : `story_wall_kind_${k}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : moments.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <Leaf className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">{t("story_wall_public_empty")}</p>
        </div>
      ) : (
        <div className="columns-1 gap-3 sm:columns-2 [&>*]:mb-3">
          {moments.map((m) => (
            <button key={m.id} onClick={() => navigate(`/app/story-wall/moment/${m.id}`)} data-testid={`swp-card-${m.id}`}
              className="block w-full break-inside-avoid rounded-2xl p-4 text-left transition-transform hover:-translate-y-0.5" style={{ backgroundColor: m.color_accent || "hsl(var(--secondary))" }}>
              <span className="inline-block rounded-full bg-white/50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-foreground">{t(`story_wall_kind_${m.kind}`)}</span>
              {m.metric_label ? <p className="mt-1.5 font-display text-2xl text-foreground">{m.metric_label}</p> : null}
              <p className="mt-1 font-semibold text-foreground">{m.title}</p>
              <p className="mt-0.5 text-sm leading-snug text-foreground/90">{m.body}</p>
              <p className="mt-2 text-[11px] italic text-foreground/70">
                {m.coach_display ? t("story_wall_by_coach", { name: m.coach_display }) : t("story_wall_by_coach_anonymous")}
                {m.client_display ? ` · ${m.client_display}` : ""}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
